import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import SalesFilters from './SalesFilters';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { Button, Typography } from '@mui/material';
import { DownloadIcon, X } from 'lucide-react';
import ProjectBreakdownTab from './ProjectBreakdownTab';
import SimpleTabs from './SimpleTabs';
import { Tooltip } from '@material-tailwind/react';
import { useUser } from '../../context/UserContext';
import { getCustomerSalesInstallment } from '../../services/sales';

function OverallSales({ data }) {
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedConfig, setSelectedConfig] = useState('all');
    const [selectedBuilding, setSelectedBuilding] = useState('all');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [timePeriod, setTimePeriod] = useState('all');
    const gridRef = useRef(null);
    const [projectFilter, setProjectFilter] = useState(''); // Initialize to an empty string
    const { userInfo } = useUser(); // Get userInfo from context
    const [installmentData, setInstallmentData] = useState([]); // Add state for installment data

    const localStorageKeyPrefix = "overallSales_"; // Add a unique prefix

    const [activeProjectTabs, setActiveProjectTabs] = useState(() => {
        const storedTabs = localStorage.getItem(`${localStorageKeyPrefix}activeProjectTabs`);
        return storedTabs ? JSON.parse(storedTabs) : {};
    });

    const [nextTabId, setNextTabId] = useState(() => {
        const storedId = localStorage.getItem(`${localStorageKeyPrefix}nextTabId`);
        return storedId ? parseInt(storedId, 10) : 1;
    });

    const [activeTabIndex, setActiveTabIndex] = useState(() => {
        const storedIndex = localStorage.getItem(`${localStorageKeyPrefix}activeTabIndex`);
        return storedIndex ? parseInt(storedIndex, 10) : 0;
    });

    // Fetch installment data when component mounts
    useEffect(() => {
        const fetchInstallmentData = async () => {
            try {
                // Pass userInfo as customerId to the API call
                const response = await getCustomerSalesInstallment({ customerId: userInfo.loginIdentifierDetails.customer.customerId });
                setInstallmentData(response || []);
            } catch (error) {
                console.error('Error fetching installment data:', error);
                setInstallmentData([]);
            }
        };

        fetchInstallmentData();
    }, [userInfo]);

    // Persist state to localStorage
    useEffect(() => {
        localStorage.setItem(`${localStorageKeyPrefix}activeProjectTabs`, JSON.stringify(activeProjectTabs));
    }, [activeProjectTabs]);

    useEffect(() => {
        localStorage.setItem(`${localStorageKeyPrefix}nextTabId`, nextTabId.toString());
    }, [nextTabId]);

    useEffect(() => {
        localStorage.setItem(`${localStorageKeyPrefix}activeTabIndex`, activeTabIndex.toString());
    }, [activeTabIndex]);

    // Function to clear localStorage items
    const clearLocalStorage = useCallback(() => {
        localStorage.removeItem(`${localStorageKeyPrefix}activeProjectTabs`);
        localStorage.removeItem(`${localStorageKeyPrefix}nextTabId`);
        localStorage.removeItem(`${localStorageKeyPrefix}activeTabIndex`);
    }, []);

    // Clear localStorage on logout
    useEffect(() => {
        const handleLogout = () => {
            clearLocalStorage();
            setActiveProjectTabs({});
            setNextTabId(1);
            setActiveTabIndex(0);
        };

        window.addEventListener('logout', handleLogout);
        return () => {
            window.removeEventListener('logout', handleLogout);
        };
    }, [clearLocalStorage, setActiveProjectTabs, setNextTabId, setActiveTabIndex]);

    const customerSales = data;

    const getConfiguration = (item) => {
        return item?.propertyConfigData?.configName || 'N/A';
    };

    const getBuildingName = (item) => {
        return item?.buildingData?.buildingName || 'N/A';
    };

    // Function to calculate expected amount from installments
    const calculateExpectedAmountFromInstallments = useCallback((customerSalesId) => {
        if (!installmentData || installmentData.length === 0) return 0;
        
        const projectInstallments = installmentData.filter(
            installment => installment.customerSales?.customerSalesId === customerSalesId
        );
        
        return projectInstallments.reduce((total, installment) => {
            return total + (parseFloat(installment.projectedAmount) || 0);
        }, 0);
    }, [installmentData]);

    const enhancedData = useMemo(() => {
        return customerSales?.map(item => {
            const projectName = item?.customerSale?.propertyData?.projectPhase?.project?.projectName ||
                item?.propertyData?.project?.projectName ||
                item?.project?.projectName ||
                'Unknown Project';

            const project = item?.customerSale?.propertyData?.projectPhase?.project ||
                item?.propertyData?.project ||
                item?.project || {};

            const customerSalesId = item?.customerSalesId || item?.customerSale?.customerSalesId;
            const expectedAmountFromInstallments = calculateExpectedAmountFromInstallments(customerSalesId);

            return {
                ...item,
                projectName: projectName,
                configuration: getConfiguration(item),
                saleDate: item?.customerSale?.saleDate || item?.saleDate || 'N/A',
                building: getBuildingName(item),
                unitsSold: 1,
                totalSalesValue: item?.customerSale?.basePrice || item?.basePrice || 0,
                expectedSaleAmount: expectedAmountFromInstallments || item?.customerSale?.expectedSaleAmount || item?.expectedSaleAmount || 0,
                project: project,
            };
        }) || [];
    }, [customerSales, calculateExpectedAmountFromInstallments]);

    const uniqueConfigs = useMemo(() => ['all', ...new Set(enhancedData.map(item => item.configuration))], [enhancedData]);
    const uniqueBuildings = useMemo(() => ['all', ...new Set(enhancedData.map(item => item.building))], [enhancedData]);

    const uniqueProjects = useMemo(() => {
        const projects = new Set(enhancedData.map(item => item.projectName));
        return [...projects];
    }, [enhancedData]);


    const filteredData = useMemo(() => {
        return enhancedData.filter(item => {
            const searchMatch = item?.projectName?.toLowerCase().includes(searchTerm.toLowerCase());
            const configMatch = selectedConfig === 'all' || item.configuration === selectedConfig;
            const buildingMatch = selectedBuilding === 'all' || item.building === selectedBuilding;
            const projectMatch = !projectFilter || item.projectName === projectFilter;

            let dateMatch = true;
            if (startDate && endDate) {
                if (item.saleDate) {
                    dateMatch = item.saleDate >= startDate && item.saleDate <= endDate;
                } else {
                    dateMatch = false;
                }
            }

            return searchMatch && configMatch && buildingMatch && projectMatch && dateMatch;
        });
    }, [enhancedData, searchTerm, selectedConfig, selectedBuilding, startDate, endDate, projectFilter]);

    const cumulativeData = useMemo(() => {
        const grouped = {};

        filteredData.forEach(item => {
            const key = item.projectName;
            if (!grouped[key]) {
                grouped[key] = {
                    projectName: item.projectName,
                    unitsSold: 0,
                    totalSalesValue: 0,
                    expectedSaleAmount: 0,
                    project: item.project,
                };
            }
            grouped[key].unitsSold += item.unitsSold;
            grouped[key].totalSalesValue += item.totalSalesValue;
            grouped[key].expectedSaleAmount += item.expectedSaleAmount;
        });

        return Object.values(grouped);
    }, [filteredData]);

    const ProjectLink = useCallback(({ project }) => {
        return (
            <Tooltip placement="right">
                <Tooltip.Trigger>
                    <button
                        onClick={() => {
                            setActiveProjectTabs((prevTabs) => {
                                const newTabId = nextTabId;
                                setNextTabId(nextTabId + 1);

                                const updatedTabs = {
                                    ...prevTabs,
                                    [project.projectName]: {
                                        id: newTabId,
                                        projectName: project.projectName,
                                        project: project,
                                    },
                                };

                                const newTabIndex = 1 + Object.keys(updatedTabs).length - 1;
                                setTimeout(() => {
                                    setActiveTabIndex(newTabIndex);
                                }, 0);
                                return updatedTabs;
                            });
                        }}
                        className="text-blue-500 hover:underline focus:outline-none"
                    >
                        {project.projectName}
                    </button>
                </Tooltip.Trigger>
                <Tooltip.Content className="bg-slate-800 text-white font-normal text-sm max-w-xs w-48">
                    Click to view project breakdown for {project.projectName}
                    <Tooltip.Arrow />
                </Tooltip.Content>
            </Tooltip>
        );
    }, [nextTabId, setActiveProjectTabs, setActiveTabIndex]);

    const columnDefs = useMemo(() => [
        {
            field: 'projectName',
            headerName: 'Project Name',
            cellRenderer: (params) => <ProjectLink project={params.data.project} />,
        },
        { field: 'unitsSold', headerName: 'Total Units Sold', valueFormatter: params => params.value.toLocaleString() },
        { field: 'totalSalesValue', headerName: 'Total Sales Value (₹)', valueFormatter: params => '₹' + params.value.toLocaleString() },
        { field: 'expectedSaleAmount', headerName: 'Expected Sale Amount (₹)', valueFormatter: params => '₹' + params.value.toLocaleString() },
    ], [ProjectLink]);

    const defaultColDef = useMemo(() => ({
        sortable: true,
        filter: true,
        resizable: true,
    }), []);

    const onGridReady = useCallback((params) => {
        params.api.sizeColumnsToFit();
    }, []);

    const exportToCsv = useCallback(() => {
        const csvExportParams = {
            fileName: 'overall_sales.csv',
        };

        gridRef.current.api.exportDataAsCsv(csvExportParams);
    }, []);

    const closeProjectTab = useCallback((projectName) => {
        setActiveProjectTabs((prevTabs) => {
            const updatedTabs = { ...prevTabs };
            delete updatedTabs[projectName];
            localStorage.setItem(`${localStorageKeyPrefix}activeProjectTabs`, JSON.stringify(updatedTabs));

            if (Object.keys(updatedTabs).length === 0) {
                localStorage.removeItem(`${localStorageKeyPrefix}nextTabId`);
                localStorage.removeItem(`${localStorageKeyPrefix}activeTabIndex`);
                setNextTabId(1);
                setActiveTabIndex(0);
            }
            return updatedTabs;
        });
        setActiveTabIndex(0);
    }, [setActiveProjectTabs, setActiveTabIndex]);

    const tabData = useMemo(() => {
        const projectTabs = Object.entries(activeProjectTabs).map(([projectName, tab]) => ({
            label: (
                <div className="flex items-center">
                    {tab.projectName}
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            closeProjectTab(projectName);
                        }}
                        className="ml-2"
                    >
                        <X size={16} />
                    </button>
                </div>
            ),
            content: <ProjectBreakdownTab project={tab.project} allCustomerSales={customerSales} />,
            id: tab.id,
        }));

        return [
            {
                label: 'Overall Sales',
                content: (
                    <>
                        <div className="flex items-center gap-4 mb-2">
                            <SalesFilters
                                searchTerm={searchTerm}
                                setSearchTerm={setSearchTerm}
                                selectedConfig={selectedConfig}
                                setSelectedConfig={setSelectedConfig}
                                selectedBuilding={selectedBuilding}
                                setSelectedBuilding={setSelectedBuilding}
                                startDate={startDate}
                                setStartDate={setStartDate}
                                endDate={endDate}
                                setEndDate={setEndDate}
                                timePeriod={timePeriod}
                                setTimePeriod={setTimePeriod}
                                uniqueConfigs={uniqueConfigs}
                                uniqueBuildings={uniqueBuildings}
                                showSearchTerm={true}
                                showConfigFilter={false}
                                uniqueProjects={uniqueProjects}
                                projectFilter={projectFilter}
                                setProjectFilter={setProjectFilter}
                            />
                            <button className="flex items-center gap-2 font-medium text-sm text-white bg-green-600 hover:bg-green-700 p-2 rounded shadow-md" onClick={exportToCsv}>
                                <DownloadIcon size={18} /> Export
                            </button>
                        </div>
                        <div className="ag-theme-quartz font-normal mt-4" style={{ height: 500, width: '100%' }}>
                            <AgGridReact
                                ref={gridRef}
                                rowData={cumulativeData}
                                columnDefs={columnDefs}
                                defaultColDef={defaultColDef}
                                rowSelection="single"
                                animateRows={true}
                                onGridReady={onGridReady}
                                onFirstDataRendered={params => {
                                    params.api.sizeColumnsToFit();
                                }}
                            />
                        </div>
                    </>
                ),
                id: 0,
            },
            ...projectTabs,
        ];
    }, [activeProjectTabs, closeProjectTab, cumulativeData, columnDefs, customerSales, exportToCsv, searchTerm, selectedConfig, selectedBuilding, startDate, endDate, timePeriod, uniqueConfigs, uniqueBuildings, uniqueProjects, projectFilter]);

    return (
        <div id="overallSales" className="p-2">
            <SimpleTabs tabs={tabData} activeTab={activeTabIndex} setActiveTab={setActiveTabIndex} />
        </div>
    );
}

export default OverallSales;